import React from "react";
import CountUp from "react-countup";

import { useInView } from "react-intersection-observer";
import { motion, AnimatePresence } from "framer-motion";
import { fadeIn } from "../variants";

import { useEffect, useState, useRef } from "react";

const Contact = () => {
  const [ref, inView] = useInView({
    threshold: 0.5,
  });

  const [scrollDirection, setScrollDirection] = useState("up");
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showInterests, setShowInterests] = useState(false);
  const interestList = [
    {
      title: "Photography",
      description: "Capturing moments and landscapes",
      icon: "📸",
      details: "Love exploring different photography techniques and styles",
    },
    {
      title: "Hiking",
      description: "Exploring nature trails",
      icon: "🏃",
      details: "Completed multiple challenging trails and mountain climbs",
    },
    {
      title: "Cooking",
      description: "Creating delicious meals",
      icon: "👨‍🍳",
      details: "Specializing in Asian fusion cuisine",
    },
    {
      title: "Reading",
      description: "Books and articles",
      icon: "📚",
      details: "Favorite genres: Science Fiction and Philosophy",
    },
    {
      title: "Music",
      description: "Playing guitar and piano",
      icon: "🎸",
      details: "Self-taught musician with 5 years of experience",
    },
    {
      title: "Travel",
      description: "Exploring new places",
      icon: "✈️",
      details: "Visited 10+ countries and counting",
    },
  ];

  const [showMountains, setShowMountains] = useState(false);
  const mountainList = [
    "Fuji",
    "Mt. Baldy",
    "Huangshan",
    "Rainier",
    "Half Dome",
    "San Gorgonio",
  ];

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY) {
        setScrollDirection("down");
      } else if (currentScrollY < lastScrollY) {
        setScrollDirection("up");
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 4; // 拖动速度因子
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  useEffect(() => {
    if (showMountains && scrollRef.current) {
      setTimeout(() => {
        const container = scrollRef.current;
        const firstItem = container.querySelector("li");
        if (firstItem) {
          const containerWidth = container.offsetWidth;
          const itemWidth = firstItem.offsetWidth;
          container.scrollTo({
            left: firstItem.offsetLeft - (containerWidth - itemWidth) / 2,
            behavior: "smooth",
          });
        }
      }, 100); // 等动画挂载后再执行
    }
  }, [showMountains]);

  const scrollByAmount = 320; // 每次滚动的距离

  const handleScrollLeft = (ref) => {
    if (ref.current) {
      ref.current.scrollBy({ left: -scrollByAmount, behavior: "smooth" });
    }
  };

  const handleScrollRight = (ref) => {
    if (ref.current) {
      ref.current.scrollBy({ left: scrollByAmount, behavior: "smooth" });
    }
  };

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseMoveContainer = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const width = rect.width;
    const zone = x < width * 0.3 ? "left" : x > width * 0.7 ? "right" : null;

    setMousePos({ x, y, zone });
  };

  return (
    <section className="section" id="interests">
      <div className="container mx-auto" ref={ref}>
        <div className="flex flex-col items-center justify-center">
          <motion.h2
            variants={fadeIn("down", 0.2)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: false, amount: 0.2 }}
            className="text-4xl lg:text-6xl font-bold mb-4"
          >
            Beyond Coding
          </motion.h2>
          <p className="text-lg lg:text-xl text-center max-w-2xl">
            When I'm not coding, I'm exploring my passions and interests. Here's
            a glimpse into my life outside of technology.
          </p>

          <div className="mt-4">
            <span className="text-3xl lg:text-5xl font-bold mx-auto">
              Interests & Hobbies{" "}
              <span
                className="text-[40px] lg:text-[50px] mt-2 dark:text-white text-black cursor-pointer select-none"
                onClick={() => setShowInterests((prev) => !prev)}
              >
                {inView ? (
                  <CountUp start={0} end={6} duration={5} delay={0} />
                ) : (
                  "X"
                )}
              </span>{" "}
              and counting
            </span>
          </div>

          <div className="w-full max-w-[400px] lg:max-w-[800px] mx-auto relative">
            <AnimatePresence mode="wait">
              {showInterests && (
                <motion.div
                  key="interestList"
                  onMouseMove={handleMouseMoveContainer}
                  onMouseLeave={() => setMousePos(null)}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.5 }}
                  className="relative w-full overflow-hidden"
                >
                  {/* Left arrow */}
                  {mousePos?.zone === "left" && (
                    <motion.button
                      onClick={() => handleScrollLeft(scrollRef)}
                      style={{
                        left: mousePos.x,
                        top: mousePos.y,
                        transform: "translate(-50%, -50%)",
                      }}
                      className="absolute z-10 rounded-full"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="size-[100px]"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18"
                        />
                      </svg>
                    </motion.button>
                  )}

                  {/* Interest cards */}
                  <motion.ul
                    ref={scrollRef}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={handleMouseLeave}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                    variants={fadeIn("down", 0.2)}
                    initial="hidden"
                    whileInView={"show"}
                    viewport={{ once: false, amount: 0.2 }}
                    className="mt-4 flex gap-4 overflow-x-auto whitespace-nowrap pl-[50px] pr-[50px] h-[300px] cursor-grab active:cursor-grabbing select-none scroll-smooth snap-x snap-mandatory items-center"
                  >
                    {interestList.map((interest, index) => (
                      <li
                        key={index}
                        className="bg-white/10 px-6 py-4 rounded-lg backdrop-blur text-sm shadow-md dark:shadow-[0_4px_8px_rgba(255,255,255,.25)] inline-block w-full flex-shrink-0 snap-center h-[290px] flex-col items-center justify-center gap-2"
                      >
                        <span className="text-4xl mb-2">{interest.icon}</span>
                        <h3 className="text-xl font-bold">{interest.title}</h3>
                        <p className="text-center text-gray-300">
                          {interest.description}
                        </p>
                        <p className="text-center text-sm text-gray-400 mt-2">
                          {interest.details}
                        </p>
                      </li>
                    ))}
                  </motion.ul>

                  {/* Right arrow */}
                  {mousePos?.zone === "right" && (
                    <motion.button
                      style={{
                        left: mousePos.x,
                        top: mousePos.y,
                        transform: "translate(-50%, -50%)",
                      }}
                      onClick={() => handleScrollRight(scrollRef)}
                      className="absolute z-10 rounded-full"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="size-[100px]"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
                        />
                      </svg>
                    </motion.button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
